import React, { useState } from 'react';
import { employer, earningCodes, deductionCodes, taxCodes, departments } from '../data/mockData';

export default function EmployerConfig() {
  const [tab, setTab] = useState('earnings');

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Employer Configuration</h2>
          <p>Client ID: {employer.clientId} · EIN: {employer.ein} · Fetched via Step 1</p>
        </div>
        <span className="badge badge-success" style={{ padding: '6px 14px' }}>✓ Synced</span>
      </div>

      {/* Employer Summary */}
      <div className="card section-gap">
        <div className="card-header"><h2>Client Profile</h2></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {[
              ['Company Name', employer.companyName],
              ['Client ID', employer.clientId],
              ['EIN', employer.ein],
              ['Employer Unique ID', employer.employerUniqueId],
              ['Direct Deposit Enabled', employer.isDirectDepositEnabled ? 'Yes' : 'No'],
              ['Processing Mode', employer.processingMode],
              ['Pay Period Start', employer.payPeriodStart],
              ['Pay Period End', employer.payPeriodEnd],
              ['Pipeline Run ID', employer.lastRunId],
            ].map(([label, value]) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: 'var(--ink-4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', fontFamily: label.includes('ID') || label.includes('EIN') || label.includes('Run') ? 'var(--font-mono)' : undefined, fontSize: label.includes('ID') || label.includes('EIN') || label.includes('Run') ? 12 : 13.5 }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-list">
        {['earnings', 'deductions', 'taxes', 'departments'].map(t => (
          <button key={t} className={`tab-btn ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t === 'earnings' ? `Earning Codes (${earningCodes.length})` :
             t === 'deductions' ? `Deduction Codes (${deductionCodes.length})` :
             t === 'taxes' ? `Tax Codes (${taxCodes.length})` :
             `Departments (${departments.length})`}
          </button>
        ))}
      </div>

      {tab === 'earnings' && (
        <div className="card">
          <div className="card-header"><h2>Employer Earning Codes</h2><span className="card-count">GET /api/v1/employer/Earnings</span></div>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Code</th><th>Description</th><th>Calc Method</th><th>Multiplier</th><th>Notes</th></tr></thead>
              <tbody>
                {earningCodes.map(ec => (
                  <tr key={ec.code}>
                    <td><span className="badge badge-primary mono">{ec.code}</span></td>
                    <td style={{ fontWeight: 600 }}>{ec.description}</td>
                    <td><span className="badge badge-neutral mono">{ec.calcMethod}</span></td>
                    <td className="mono">{ec.multiplier ? `×${ec.multiplier}` : '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {ec.calcMethod === 'H' ? 'Rate-based (hourly)' :
                       ec.calcMethod === 'Z' ? 'Flat amount' :
                       ec.calcMethod === 'S' ? 'Salary basis' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'deductions' && (
        <div className="card">
          <div className="card-header"><h2>Employer Deduction Codes</h2><span className="card-count">GET /api/v1/employer/deductions</span></div>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Code</th><th>Description</th><th>Calc Method</th><th>Rate</th><th>Flat Amount</th></tr></thead>
              <tbody>
                {deductionCodes.map(dc => (
                  <tr key={dc.code}>
                    <td><span className="badge badge-neutral mono">{dc.code}</span></td>
                    <td style={{ fontWeight: 600 }}>{dc.description}</td>
                    <td><span className="badge badge-neutral mono">{dc.calcMethod}</span></td>
                    <td className="mono">{dc.rate ? `${(dc.rate * 100).toFixed(0)}%` : '—'}</td>
                    <td className="mono">{dc.amount ? `$${dc.amount.toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'taxes' && (
        <div className="card">
          <div className="card-header"><h2>Tax Codes</h2><span className="card-count">GET /api/v1/employer/taxes</span></div>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Code</th><th>Description</th><th>Jurisdiction</th></tr></thead>
              <tbody>
                {taxCodes.map(tc => (
                  <tr key={tc.code}>
                    <td><span className="badge badge-neutral mono">{tc.code}</span></td>
                    <td style={{ fontWeight: 600 }}>{tc.description}</td>
                    <td>
                      <span className={`badge badge-${tc.jurisdiction === 'Federal' ? 'primary' : tc.jurisdiction === 'State' ? 'info' : 'neutral'}`}>
                        {tc.jurisdiction}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'departments' && (
        <div className="card">
          <div className="card-header"><h2>Departments</h2><span className="card-count">GET /api/v1/employer/departments</span></div>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead><tr><th>Department ID</th><th>Name</th></tr></thead>
              <tbody>
                {departments.map(d => (
                  <tr key={d.id}>
                    <td><span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{d.id}</span></td>
                    <td style={{ fontWeight: 600 }}>{d.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
